/**
 * DOM utility functions
 * Centralizes DOM manipulation and caching
 *
 * IMPORTANT: Uses per-window caching to support same-origin iframes.
 * Each window/iframe context gets its own isolated element cache.
 */
export default class DOMUtils {
  // Per-window cache storage (WeakMap allows GC when windows close)
  static _windowCaches = new WeakMap();

  /**
   * Get the cache object for the current window context.
   * @returns {Object} Cache object with elementCache, isMobile, isAndroid
   */
  static _getWindowCache() {
    const win = typeof window !== 'undefined' ? window : globalThis;
    if (!this._windowCaches.has(win)) {
      this._windowCaches.set(win, {
        elementCache: new Map(),
        isMobile: null,
        isAndroid: null
      });
    }
    return this._windowCaches.get(win);
  }

  // Convenience accessors for backwards compatibility
  static get elementCache() { return this._getWindowCache().elementCache; }
  static get _isMobile() { return this._getWindowCache().isMobile; }
  static set _isMobile(v) { this._getWindowCache().isMobile = v; }
  static get _isAndroid() { return this._getWindowCache().isAndroid; }
  static set _isAndroid(v) { this._getWindowCache().isAndroid = v; }

  /**
   * Detect if running on a mobile/touch device
   * Uses multiple heuristics for reliable detection
   * @return {boolean} True if mobile device
   */
  static isMobile() {
    if (this._isMobile !== null) {
      return this._isMobile;
    }

    // Check for touch capability
    const hasTouch = 'ontouchstart' in window ||
                     navigator.maxTouchPoints > 0 ||
                     navigator.msMaxTouchPoints > 0;

    // Check user agent for mobile keywords
    const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Check for coarse pointer (touch screens)
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

    // Check for hover capability (most mobile devices don't have true hover)
    const noHover = window.matchMedia('(hover: none)').matches;

    // Consider it mobile if it has touch + coarse pointer, or matches mobile UA
    this._isMobile = (hasTouch && hasCoarsePointer) || mobileUA || (hasTouch && noHover);

    return this._isMobile;
  }

  /**
   * Detect if running on Android
   * Used for platform-specific rendering workarounds
   * @return {boolean} True if Android device
   */
  static isAndroid() {
    if (this._isAndroid !== null) {
      return this._isAndroid;
    }
    this._isAndroid = /Android/i.test(navigator.userAgent);
    return this._isAndroid;
  }

  /**
   * Check if the device has limited GPU/rendering capabilities
   * Mobile devices often have issues with large rotated elements
   * @return {boolean} True if device likely has rendering limitations
   */
  static hasRenderingLimitations() {
    return this.isMobile();
  }

  /**
   * Get the effective zoom for materials/rendering.
   * On Android, zoom is baked into calculations, so materials need the actual zoom.
   * On other platforms, CSS scale on #world handles zoom, so materials use 1.
   * @param {number} actualZoom - The actual camera zoom level
   * @return {number} The zoom to use for materials (1 or actualZoom)
   */
  static getEffectiveZoom(actualZoom) {
    return this.isAndroid() ? actualZoom : 1;
  }

  /**
   * Reset the mobile detection cache (useful for testing)
   */
  static resetMobileCache() {
    this._isMobile = null;
  }

  /**
   * Create a DOM element with class and styles
   * @param {string} tag - HTML tag name
   * @param {string} className - CSS class name (optional)
   * @param {Object} styles - CSS styles object (optional)
   * @return {HTMLElement} Created element
   */
  static createElement(tag, className = '', styles = {}) {
    const element = document.createElement(tag);
    if (className) {
      element.className = className;
    }
    this.setStyles(element, styles);
    return element;
  }

  /**
   * Create a button element
   * @param {string} text - Button text
   * @param {Function} onClick - Click handler
   * @param {Object} styles - CSS styles object (optional)
   * @return {HTMLButtonElement} Created button
   */
  static createButton(text, onClick, styles = {}) {
    const button = this.createElement('button', '', styles);
    button.textContent = text;
    if (onClick) {
      button.addEventListener('click', onClick);
    }
    return button;
  }

  /**
   * Create an input element
   * @param {string} type - Input type
   * @param {*} value - Initial value
   * @param {Function} onChange - Change handler
   * @param {Object} styles - CSS styles object (optional)
   * @return {HTMLInputElement} Created input
   */
  static createInput(type, value, onChange, styles = {}) {
    const input = this.createElement('input', '', styles);
    input.type = type;
    input.value = value;
    if (onChange) {
      input.addEventListener('input', onChange);
    }
    return input;
  }

  /**
   * Create a select element
   * @param {Array} options - Array of {value, text} objects
   * @param {*} selected - Selected value
   * @param {Function} onChange - Change handler
   * @return {HTMLSelectElement} Created select
   */
  static createSelect(options, selected, onChange) {
    const select = this.createElement('select');
    options.forEach(opt => {
      const option = this.createElement('option');
      option.value = opt.value;
      option.textContent = opt.text || opt.value;
      if (opt.value === selected) {
        option.selected = true;
      }
      select.appendChild(option);
    });
    if (onChange) {
      select.addEventListener('change', onChange);
    }
    return select;
  }

  /**
   * Create a slider (range input) element
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @param {number} step - Step value
   * @param {number} value - Initial value
   * @param {Function} onChange - Change handler
   * @return {HTMLInputElement} Created slider
   */
  static createSlider(min, max, step, value, onChange) {
    const slider = this.createElement('input');
    slider.type = 'range';
    slider.min = min;
    slider.max = max;
    slider.step = step;
    slider.value = value;
    if (onChange) {
      slider.addEventListener('input', onChange);
    }
    return slider;
  }

  /**
   * Create a label element
   * @param {string} text - Label text
   * @param {HTMLElement} forElement - Associated input element (optional)
   * @return {HTMLLabelElement} Created label
   */
  static createLabel(text, forElement = null) {
    const label = this.createElement('label');
    label.textContent = text;
    if (forElement && forElement.id) {
      label.htmlFor = forElement.id;
    }
    return label;
  }

  /**
   * Remove all children from an element
   * @param {HTMLElement} element - Parent element
   */
  static removeAllChildren(element) {
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }
  }

  /**
   * Set multiple styles on an element
   * @param {HTMLElement} element - Target element
   * @param {Object} styles - CSS styles object
   */
  static setStyles(element, styles) {
    Object.keys(styles).forEach(key => {
      element.style[key] = styles[key];
    });
  }

  /**
   * Add multiple classes to an element
   * @param {HTMLElement} element - Target element
   * @param {Array<string>} classes - Array of class names
   */
  static addClasses(element, classes) {
    classes.forEach(cls => element.classList.add(cls));
  }

  /**
   * Remove multiple classes from an element
   * @param {HTMLElement} element - Target element
   * @param {Array<string>} classes - Array of class names
   */
  static removeClasses(element, classes) {
    classes.forEach(cls => element.classList.remove(cls));
  }

  /**
   * Cache an element by selector
   * @param {string} selector - CSS selector
   * @return {HTMLElement} Cached element
   */
  static cacheElement(selector) {
    if (!this.elementCache.has(selector)) {
      const element = document.querySelector(selector);
      if (element) {
        this.elementCache.set(selector, element);
      }
    }
    return this.elementCache.get(selector);
  }

  /**
   * Get element by ID (with caching)
   * @param {string} id - Element ID
   * @return {HTMLElement} Element
   */
  static getElementById(id) {
    return this.cacheElement(`#${id}`);
  }

  /**
   * Clear the element cache
   */
  static clearCache() {
    this.elementCache.clear();
  }

  /**
   * Show an element
   * @param {HTMLElement} element - Element to show
   * @param {string} display - Display style (default: 'block')
   */
  static show(element, display = 'block') {
    element.style.display = display;
  }

  /**
   * Hide an element
   * @param {HTMLElement} element - Element to hide
   */
  static hide(element) {
    element.style.display = 'none';
  }

  /**
   * Toggle element visibility
   * @param {HTMLElement} element - Element to toggle
   * @param {string} display - Display style when shown (default: 'block')
   */
  static toggle(element, display = 'block') {
    if (element.style.display === 'none') {
      this.show(element, display);
    } else {
      this.hide(element);
    }
  }

  /**
   * Set element text content safely
   * @param {HTMLElement} element - Target element
   * @param {string} text - Text content
   */
  static setText(element, text) {
    element.textContent = text;
  }

  /**
   * Set element HTML content safely
   * @param {HTMLElement} element - Target element
   * @param {string} html - HTML content
   */
  static setHTML(element, html) {
    element.innerHTML = html;
  }

  /**
   * Create a div with optional class and styles
   * @param {string} className - CSS class name (optional)
   * @param {Object} styles - CSS styles object (optional)
   * @return {HTMLDivElement} Created div
   */
  static createDiv(className = '', styles = {}) {
    return this.createElement('div', className, styles);
  }

  /**
   * Create a span with optional class and styles
   * @param {string} className - CSS class name (optional)
   * @param {Object} styles - CSS styles object (optional)
   * @return {HTMLSpanElement} Created span
   */
  static createSpan(className = '', styles = {}) {
    return this.createElement('span', className, styles);
  }

  /**
   * Append multiple children to a parent element
   * @param {HTMLElement} parent - Parent element
   * @param {Array<HTMLElement>} children - Array of child elements
   */
  static appendChildren(parent, children) {
    children.forEach(child => parent.appendChild(child));
  }
}
